<template>
  <div class="aside-menu-tab">
    <component
      :is="getTabTag(tab)"
      :class="[
        'aside-menu-tab__link',
        {
          'aside-menu-tab__link--child': tab.isChild,
          'aside-menu-tab__link--click': getIsClickable(tab),
        },
      ]"
      :to="tab.to || false"
      :href="tab.externalLink || false"
      :target="tab.externalLink ? '_blank' : false"
      v-on="tab.events || {}"
    >
      <div :class="['aside-menu-tab__text', { 'aside-menu-tab__text--active': getIsActive(tab) }]">
        <div class="aside-menu-tab__text-inner">
          <span v-if="tab.isChild" class="aside-menu-tab__dot"></span>
          <span v-html="tab.transformText ? tab.transformText(tab.text, $style) : tab.text"></span>
        </div>
      </div>
    </component>
    <Accordion v-if="tab.children" class="aside-menu-tab__accordion" :isOpen="isAccordionOpen">
      <div class="aside-menu-tab__accordion-inner">
        <template v-for="(child, childIndex) in tab.children">
          <template v-if="child.children">
            <AsideMenuTab
              :key="`tab-${index}-${childIndex}`"
              class="aside-menu-tab__child aside-menu-tab__child--of-child"
              :index="index"
              :tab="child"
              :isActiveOnCurrentRoute="child.isActiveOnCurrentRoute"
            />
          </template>
          <div v-else :key="`accordion-${index}-${childIndex}`" class="aside-menu-tab__child">
            <component
              :is="getTabTag(child)"
              :class="[
                'aside-menu-tab__link',
                'aside-menu-tab__link--child',
                {
                  'aside-menu-tab__link--space-bottom':
                    tab.isChild && childIndex === tab.children.length - 1,
                  'aside-menu-tab__link--click': getIsClickable(child),
                },
              ]"
              :to="child.to || false"
              v-on="child.events || {}"
            >
              <div
                :class="[
                  'aside-menu-tab__text',
                  {
                    'aside-menu-tab__text--active': getIsActive(child),
                    'aside-menu-tab__text--light': child.isLight,
                    'aside-menu-tab__text--italic': child.isItalic,
                  },
                ]"
              >
                <div class="aside-menu-tab__text-inner">
                  <span class="aside-menu-tab__dot"></span>
                  {{ child.text }}
                </div>
              </div>
            </component>
          </div>
        </template>
      </div>
    </Accordion>
  </div>
</template>

<script>
import Accordion from '@/components/Accordion.vue'

export default {
  name: 'AsideMenuTab',
  components: {
    Accordion,
  },

  props: {
    index: {
      type: Number,
      required: true,
    },

    tab: {
      type: Object,
      default: () => ({}),
    },

    isActiveOnCurrentRoute: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isAccordionOpen() {
      if (this.tab.hasChildrenVisible) return this.tab.hasChildrenVisible.call(this)

      if (this.isActiveOnCurrentRoute) return this.isCurrentRouteTo(this.tab.to)

      return true
    },
  },

  methods: {
    getTabTag(tab) {
      return tab.to ? 'nuxt-link' : 'a'
    },

    getIsClickable(tab) {
      return tab.to || tab.externalLink || tab.events?.click
    },

    getIsActive(tab) {
      if (!this.isActiveOnCurrentRoute) return tab.isActive

      return this.isCurrentRouteTo(tab.to)
    },

    isCurrentRouteTo(to) {
      return this.$route.fullPath.startsWith(this.localeRoute(to).fullPath)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/components/SiteContainer/site-container-settings';
@import './settings';

.aside-menu-tab {
  &__accordion-inner {
    padding: #{$padding-tab-y - $padding-child-tab-y} 0;
  }

  &__child {
    margin-left: 22px;
  }

  &__link {
    display: inline-block;
    padding: $padding-tab-y $padding-tab-x;
    cursor: default;

    &--child {
      padding: $padding-child-tab-y $padding-tab-x;
    }

    &--space-bottom {
      padding-bottom: $padding-child-tab-y + 5;
    }

    &--click {
      cursor: pointer;
    }
  }

  &__dot {
    position: absolute;
    background-color: currentColor;
    top: 50%;
    left: -14px;
    width: 2px;
    height: 2px;
    border-radius: 50%;
    transform: translateY(-50%);
  }

  &__text {
    position: relative;
    line-height: $line-height-normal;
    font-size: $font-size-large - 1;
    color: $color-grey-3;
    transition: color 0.3s;

    .nuxt-link-exact-active &,
    &--active {
      color: $color-brand-coral-text-dark;
    }

    &--light {
      color: $color-grey-4;
    }

    &--italic {
      font-style: italic;
    }

    @include min-screen($mq-larger) {
      font-size: $font-size-large;
    }

    .aside-menu-tab__link--click & {
      &:before,
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        background-color: $color-background;
        width: calc(100% - 10px);
        height: 1px;
        transition:
          background-color 0.3s,
          transform 0.3s cubic-bezier(0.425, 0.18, 0.2, 0.985);
        z-index: 1;
      }

      &:before {
        left: 0;
        transform: translateX($underline-animation-gap);
      }

      &:after {
        right: 0;
        transform: translateX(-$underline-animation-gap);
      }
    }
  }

  &__link--click:hover &__text {
    &:before,
    &:after {
      background-color: $color-grey-3;
    }

    &--light {
      &:before,
      &:after {
        background-color: $color-grey-4;
      }
    }

    &:before {
      transform: translateX(-1px);
    }

    &:after {
      transform: translateX(1px);
    }
  }

  .nuxt-link-exact-active:hover &__text,
  &__link--click:hover &__text--active {
    &:before,
    &:after {
      background-color: $color-brand-coral-text-dark;
    }
  }

  &__text-inner {
    position: relative;
    z-index: 2;
  }
}
</style>

<style lang="scss" module>
.textHighlighted {
  font-weight: bold;
  color: $color-text-warning;
}
</style>
